import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getActiveCustomChallenges,
  CustomChallenge,
} from "../components/weeklyChallenges/customChallenge";
import { useTheme } from "../contexts/ThemeContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StyledLoadingSpinner from "../components/ui/StyledLoadingSpinner";
import ChallengeDetailsSideNav from "./ChallengeDetailsSideNav";
import MetaHead from "../components/ui/MetaHead";
import EditChallengeModal from "../components/ui/EditChallengeModal";
import { useAuth } from "../contexts/AuthContext";
import { DeleteModal } from "../components/DeleteModal";
import { deleteChallenge } from "../services/firebase";
import Markdown from "react-markdown";
import {
  formatToEasternTime,
  formatToET,
} from "../components/utils/formatToET";
import remarkGfm from "remark-gfm";
import { addImageClass } from "../components/utils/resizeImage";
import ShareThis from "../components/utils/shareThis";

const ChallengeDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: currentId } = useParams<{ id: string }>();
  const { theme } = useTheme();

  const [allChallenges, setAllChallenges] = useState<CustomChallenge[]>([]);
  const [loaded, setLoaded] = useState<CustomChallenge[]>([]);
  const [activeSlug, setActiveSlug] = useState<string>(currentId || "");
  const [isLoading, setIsLoading] = useState(true);
  const [challengeinView, setChallengeinView] = useState<CustomChallenge>();
  const [isDeleting, setIsDeleting] = useState(false);

  const [isManualScroll, setIsManualScroll] = useState(false);
  const manualScrollTimeout = useRef<number>();

  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const initialScrollDone = useRef(false);
  const blockScrollRef = useRef(false);

  const { user, userRole } = useAuth();
  const isAdmin = userRole === "admin";
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/");
    }
  }, []);

  const availableStats = [
    { id: "qb_pts", name: "QB Fantasy Points", category: "QB Total" },
    { id: "rb_pts", name: "RB Fantasy Points", category: "RB Total" },
    { id: "wr_pts", name: "WR Fantasy Points", category: "WR Total" },
    { id: "te_pts", name: "TE Fantasy Points", category: "TE Total" },
    { id: "k_pts", name: "K Fantasy Points", category: "K Total" },
    { id: "def_pts", name: "DEF Fantasy Points", category: "DEF Total" },
    { id: "4", name: "Passing Yards", category: "Passing" },
    { id: "5", name: "Passing TDs", category: "Passing" },
    { id: "6", name: "Interceptions", category: "Passing" },
    { id: "9", name: "Rushing Yards", category: "Rushing" },
    { id: "10", name: "Rushing TDs", category: "Rushing" },
    { id: "11", name: "Receptions", category: "Receiving" },
    { id: "12", name: "Receiving Yards", category: "Receiving" },
    { id: "13", name: "Receiving TDs", category: "Receiving" },
    { id: "18", name: "Fumbles Lost", category: "Misc" },
    { id: "32", name: "Sacks", category: "Defense" },
    { id: "33", name: "Interceptions", category: "Defense" },
    { id: "34", name: "Fumble Recoveries", category: "Defense" },
    { id: "35", name: "Defensive TDs", category: "Defense" },
    { id: "36", name: "Safeties", category: "Defense" },
    { id: "37", name: "Blocked Kicks", category: "Defense" },
    { id: "38", name: "Points Allowed", category: "Defense" },
    { id: "19", name: "FG Made 0-19", category: "Kicking" },
    { id: "20", name: "FG Made 20-29", category: "Kicking" },
    { id: "21", name: "FG Made 30-39", category: "Kicking" },
    { id: "22", name: "FG Made 40-49", category: "Kicking" },
    { id: "23", name: "FG Made 50+", category: "Kicking" },
    { id: "fg_total", name: "Total FG Made", category: "Kicking" },
    { id: "29", name: "PAT Made", category: "Kicking" },
    { id: "30", name: "PAT Missed", category: "Kicking" },
    {
      id: "FG_TOTAL_MISSED",
      name: "Total Field Goals Missed",
      category: "Kicking",
    },
  ];

  const [scrollClass, setScrollClass] = useState("pageWithoutScroll");

  const handleScroll = () => {
    if (window.scrollY > 260) {
      //  Example:  change class after scrolling 50px
      setScrollClass("");
    } else {
      setScrollClass("pageWithoutScroll"); // Remove the class if not scrolled enough
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    // ...
  }, []);

  const getStatName = (statId: string) => {
    const stat = availableStats.find((s) => s.id === statId);
    return stat ? stat.name : statId;
  };

  const handleDeleteSuccess = () => {
    window.location.reload();
    // Refresh the challenge data
  };

  const handleDelete = useCallback(async (challengeId: string) => {
    setIsDeleting(true);
    try {
      await deleteChallenge(challengeId);
      setAllChallenges((prev) =>
        prev.filter((challenge) => challenge.id !== challengeId)
      );
      setIsDeleting(false);
      handleDeleteSuccess();
    } catch (error) {
      console.error("Failed to delete blog:", error);
    }
  }, []);

  // Fetch all challenges once and pre-load around the active one
  useEffect(() => {
    (async () => {
      blockScrollRef.current = true;
      initialScrollDone.current = false;
      try {
        const fetched = await getActiveCustomChallenges();
        setAllChallenges(fetched.sort((a, b) => b.year.localeCompare(a.year)));
        const idx = fetched.findIndex((c) => c.slug === currentId);
        // just load the single target (or fallback to the first)
        if (idx >= 0) {
          setLoaded([fetched[idx]]);
        } else {
          setLoaded([fetched[0]]);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
        setTimeout(() => {
          blockScrollRef.current = false; // allow scroll again
        }, 300);
      }
    })();
  }, [currentId, isEdit]);

  // Scroll into view only on initial landing
  /* useEffect(() => {
    if (!initialScrollDone.current && currentId && loaded.length) {
      const el = sectionRefs.current[currentId];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        initialScrollDone.current = true;
      }
    }
  }, [loaded, currentId]); */
  useEffect(() => {
    if (!initialScrollDone.current && currentId && loaded.length) {
      const tryScroll = () => {
        const el = sectionRefs.current[currentId];
        if (el) {
          // Check if all images in the section are loaded
          const imgs = el.querySelectorAll("img");
          let allLoaded = true;
          imgs.forEach((img) => {
            if (!img.complete) allLoaded = false;
          });
          if (allLoaded) {
            // Calculate offset to keep banner visible
            const bannerHeight =
              document.querySelector(".bannerWrap")?.getBoundingClientRect()
                .height || 0;
            const offset = bannerHeight + 100; // Add extra padding
            window.scrollTo({
              top: el.offsetTop - offset,
              behavior: "smooth",
            });
            initialScrollDone.current = true;
          } else {
            // Wait for images to load, then try again
            imgs.forEach((img) => {
              img.addEventListener("load", tryScroll, { once: true });
            });
            setTimeout(tryScroll, 1000); // fallback in case no images
          }
        } else {
          setTimeout(tryScroll, 200);
        }
      };
      tryScroll();
    }
  }, [loaded, currentId]);

  // Load a challenge into view
  const loadSlug = useCallback(
    (slug: string | undefined, atStart = false) => {
      if (loaded.some((c) => c.slug === slug)) return;
      const ch = allChallenges.find((c) => c.slug === slug);
      if (!ch) return;
      setLoaded((prev) => (atStart ? [ch, ...prev] : [...prev, ch]));
    },
    [allChallenges, loaded]
  );

  // update page title and meta accoring to the active challenge
  useEffect(() => {
    const idx = allChallenges.findIndex((c) => c.slug === activeSlug);
    setChallengeinView(allChallenges[idx]);
  }, [activeSlug]);

  // Load next on scroll to bottom
  useEffect(() => {
    const node = bottomRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && loaded.length && !blockScrollRef.current) {
          const last = loaded[loaded.length - 1].slug;
          const idx = allChallenges.findIndex((c) => c.slug === last);
          if (idx >= 0 && idx < allChallenges.length - 1) {
            loadSlug(allChallenges[idx + 1].slug);
          }
        }
      },
      { rootMargin: "200px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [loaded, allChallenges, loadSlug]);

  // Load previous on scroll to top
  useEffect(() => {
    const node = topRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && loaded.length && !blockScrollRef.current) {
          const first = loaded[0].slug;
          const idx = allChallenges.findIndex((c) => c.slug === first);
          if (idx > 0) loadSlug(allChallenges[idx - 1].slug, true);
        }
      },
      { rootMargin: "200px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [loaded, allChallenges, loadSlug]);

  // Update URL & nav on section in view without causing scroll
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (isManualScroll) return; // <-- short‑circuit while we’re in fly‑over mode

        entries.forEach((e) => {
          if (e.isIntersecting) {
            const slug = e.target.getAttribute("data-slug")!;
            if (slug !== activeSlug) {
              setActiveSlug(slug);
              window.history.replaceState(null, "", `/challenges/${slug}`);
            }
          }
        });
      },
      { rootMargin: "-50% 0px -50% 0px" }
    );

    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [loaded, activeSlug, isManualScroll]);

  if (isLoading) return <StyledLoadingSpinner />;

  const onNavigationClick = (challenge: CustomChallenge) => {
    const slug = challenge.slug;
    setIsManualScroll(true);

    const target = sectionRefs.current[slug || ""];
    if (target) {
      const bannerHeight =
        document.querySelector(".topHeader")?.getBoundingClientRect().height ||
        0;
      const offset = bannerHeight + 30;
      window.scrollTo({
        top: target.offsetTop - offset,
        behavior: "smooth",
      });
    }

    window.history.replaceState(null, "", `/challenges/${slug}`);
    clearTimeout(manualScrollTimeout.current);
    manualScrollTimeout.current = window.setTimeout(() => {
      setActiveSlug(slug || "");
      setIsManualScroll(false);
    }, 1000);
  };

  const handleEditSuccess = () => {
    setIsEdit(true);
  };

  const formatStep = (step: any) => {
    // Handle operators, turning "*" into "X"
    if (step.type === "operator") {
      return step.value === "*" ? "X" : step.value;
    }

    // Handle stat steps when we have the playerStat object
    if (step.type === "stat" && step.playerStat) {
      const { position, positionRank, statId } = step.playerStat;
      return `${position} ${positionRank} ${getStatName(statId)}`;
    }

    // Fallback to the raw value
    return step.value;
  };

  return (
    <div className="pageOuterWrapper">
      <Navbar />
      {challengeinView && (
        <MetaHead
          title={`${challengeinView.title} - Week ${challengeinView.week} Fantasy Challenge`}
          description={`Take on the ${
            challengeinView.title
          } fantasy football challenge for Week ${challengeinView.week}. ${
            challengeinView.subtitle || challengeinView.description
          }`}
          image={challengeinView.image || undefined}
          url={`https://fantasyloser.com/challenges/${challengeinView.slug}`}
          type="article"
          keywords={[
            "weekly fantasy challenge",
            "fantasy football competition",
            "fantasy league challenge",
            "week " + challengeinView.week + " challenge",
            challengeinView.title.toLowerCase(),
            "fantasy football activity",
          ]}
        />
      )}
      <div className="mainContentWrap">
        <div className="bannerWrap">
          <div className="wrapper">
            <h2>Weekly Challenges</h2>
            <div className="bannerSubTxt">
              <span>
                A layer of gamification beyond the weekly fantasy matchups,
              </span>{" "}
              designed to keep your league engaged with new ways to compete
              during the season.
            </div>
          </div>
        </div>
        <div className="contentArea">
          <div className={`wrapper ${scrollClass}`}>
            <ChallengeDetailsSideNav
              allChallenges={allChallenges}
              activeSlug={activeSlug}
              onNavigationClick={onNavigationClick}
            />
            <div className="challengeRightContent">
              <ul className="challengeList noDividers">
                {allChallenges.map((challenge, index) => (
                  <li
                    key={challenge.id}
                    id={challenge.id}
                    data-slug={challenge.slug}
                    ref={(el) => {
                      sectionRefs.current[challenge.slug || 0] = el;
                    }}
                    //className={index === 0 ? "scroll-mt-36" : "scroll-mt-16"}
                  >
                    {isAdmin && challenge.id && (
                      <div className="actionBtnWrap challengeActionBtns">
                        <EditChallengeModal
                          challengeId={challenge.id}
                          isAdmin={isAdmin}
                          onSaveSuccess={handleEditSuccess}
                          isEdit={isEdit}
                          setIsEdit={setIsEdit}
                        />
                        <DeleteModal
                          onConfirm={() => handleDelete(challenge.id || "")}
                          description="Are you sure you want to delete this challenge? This action cannot be undone."
                          isDeleting={isDeleting}
                        />
                      </div>
                    )}
                    <div className="weekItem">
                      <span>Week {challenge.week}</span>
                    </div>
                    <h3 className="mainHeading">
                      {challenge.title}
                      {challenge.subtitle && (
                        <div className="subHeading">
                          <Markdown remarkPlugins={[remarkGfm]}>
                            {challenge.subtitle}
                          </Markdown>
                        </div>
                      )}
                    </h3>

                    {challenge.image ? (
                      <div className="challengePic">
                        <img
                          src={challenge.image}
                          alt={challenge.title}
                          onLoad={(event) => {
                            addImageClass(event);
                          }}
                          style={{
                            maxHeight: challenge.imageMaxHeight
                              ? challenge.imageMaxHeight
                              : "",
                          }}
                        />
                      </div>
                    ) : null}

                    <div className="userBlkWrap">
                      {challenge.author && (
                        <>
                          <div className="userPic">
                            <img src={challenge.thumbnail} alt="Author Image" />
                          </div>
                          <div className="userCont">
                            <div className="userHeading">
                              {challenge.author}
                            </div>
                            <div className="updatedOn">
                              {formatToEasternTime(
                                challenge.dateTimeInEasternTime
                              ) ?? formatToET(challenge.createdAt)}
                            </div>
                          </div>
                        </>
                      )}
                      <div className="socialBtnWrap">
                        <ShareThis
                          title={challenge.title}
                          description={challenge.subtitle}
                          image={challenge.image || ""}
                        />
                      </div>
                    </div>
                    <Markdown remarkPlugins={[remarkGfm]}>
                      {challenge.description}
                    </Markdown>
                    <div className="challengeItem">
                      {challenge.formulaGroups.map((group, groupIndex) => (
                        <div key={groupIndex}>
                          {group.steps.map((step, stepIndex) => (
                            <React.Fragment key={stepIndex}>
                              {step.type === "operator" ? (
                                <span className="relationCalc">
                                  {formatStep(step)}
                                </span>
                              ) : (
                                <div className="relationTxt">
                                  {formatStep(step)}
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      ))}
                    </div>
                    {challenge.stakes && (
                      <div className="challengeOptions">
                        <h2>The Stakes</h2>
                        <p>
                          Your choice for what works for your league. Our
                          recommended options for this week, but as always, feel
                          free to cook up your own.
                        </p>
                        <ul className="optionList">
                          {challenge.stakes.map((stake, index) => (
                            <li key={index}>
                              <span className="tagItem">
                                Option {index + 1}
                              </span>
                              <h4>
                                <Markdown remarkPlugins={[remarkGfm]}>
                                  {stake.title}
                                </Markdown>
                              </h4>
                              <Markdown remarkPlugins={[remarkGfm]}>
                                {stake.description}
                              </Markdown>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ChallengeDetailPage;
